/**
 * ReleaseFreigabe: admin page at /transport-freigabe.
 *
 * Basin layout: header with breadcrumb, a basin bar, day chapters in a list
 * and a detail sheet with the transport contents.
 *   Basin "open"     = /api/cicd/release-candidate (one candidate per space)
 *   Basin "released" = /api/cicd/release-history   (already released)
 *
 * The release goes out as POST /api/cicd/transport/release with a mandatory
 * approvalNote. Collection stage and target (PROD) come from
 * project.stageEnvMap. No four-eyes check - a deliberate single-admin release.
 *
 * Styling: iio-slz-* classes from styles/iio-components.css, scoped under .iio.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";

// Language-dependent date format: this page is a record of proof, so a
// misread timestamp would be more than a cosmetic flaw.
const gebiet = () => (i18n.language === "en" ? "en-GB" : "de-DE");
const datumLang = (d) => d.toLocaleDateString(gebiet(), { day: "numeric", month: "long", year: "numeric" });

function dayKey(iso) {
  const d = new Date(iso);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}
function fmtDay(key, t) {
  const d = new Date(key + "T12:00:00");
  const today = dayKey(new Date().toISOString());
  const yest = dayKey(new Date(Date.now() - 864e5).toISOString());
  const datum = datumLang(d);
  if (key === today) return t("schleuse:datum.heute", { datum });
  if (key === yest) return t("schleuse:datum.gestern", { datum });
  return t("schleuse:datum.wochentag", { wochentag: d.toLocaleDateString(gebiet(), { weekday: "long" }), datum });
}
function fmtClock(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(gebiet(), { hour: "2-digit", minute: "2-digit" });
}
function fmtFull(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(gebiet(), { day: "2-digit", month: "2-digit", year: "2-digit" }) + ", "
    + d.toLocaleTimeString(gebiet(), { hour: "2-digit", minute: "2-digit" });
}

const HIST_LIMIT_START = 50;

export default function ReleaseFreigabe({ user, spaces = [] }) {
  const { t } = useTranslation(["schleuse", "freigabe", "gemeinsam"]);
  const { toasts, addToast } = useToast();
  const isAdmin = user && user.role === "admin";

  const [spaceId, setSpaceId] = useState(() => spaces[0]?.id || "");
  const [basin, setBasin] = useState("offen");

  const [candData, setCandData] = useState(null);
  const [candLoading, setCandLoading] = useState(false);

  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histLimit, setHistLimit] = useState(HIST_LIMIT_START);

  const [detail, setDetail] = useState(null); // { kind:"candidate"|"history", data:... }
  const [grund, setGrund] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [result, setResult] = useState(null);

  const escRef = useRef({});
  escRef.current = { detail };

  // Same as in the CI/CD monitor: on a direct page load the spaces have not
  // arrived yet, so spaces[0] in useState grabs nothing. Catch up once they land.
  useEffect(() => {
    if (!spaceId && spaces.length > 0) setSpaceId(spaces[0].id);
  }, [spaces, spaceId]);

  const ladeKandidat = useCallback(async (sid) => {
    if (!sid) { setCandData(null); return; }
    setCandLoading(true);
    // i18n.t instead of t inside the callbacks: the instance survives a
    // language switch, so the load effect does not re-fetch on every switch.
    try { setCandData(await api(`/api/cicd/release-candidate?spaceId=${encodeURIComponent(sid)}`)); }
    catch (e) { addToast(e?.message || i18n.t("freigabe:meldung.kandidatFehler"), "error"); setCandData(null); }
    setCandLoading(false);
  }, [addToast]);

  const ladeHistorie = useCallback(async (sid, limit) => {
    if (!sid) { setHistData(null); return; }
    setHistLoading(true);
    try {
      setHistData(await api(`/api/cicd/release-history?spaceId=${encodeURIComponent(sid)}&limit=${limit}`));
    } catch (e) {
      addToast(e?.message || i18n.t("freigabe:meldung.historieFehler"), "error");
      setHistData(null);
    }
    setHistLoading(false);
  }, [addToast]);

  useEffect(() => {
    if (!isAdmin) return;
    setResult(null); setDetail(null); setGrund(""); setHistLimit(HIST_LIMIT_START);
    ladeKandidat(spaceId);
    ladeHistorie(spaceId, HIST_LIMIT_START);
  }, [isAdmin, spaceId, ladeKandidat, ladeHistorie]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && escRef.current.detail) closeSheet(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  useEffect(() => {
    document.body.style.overflow = detail ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [detail]);

  const closeSheet = () => { setDetail(null); setGrund(""); setResult(null); };

  const project = candData?.project || histData?.project || null;
  const prodEnv = project?.stageEnvMap?.prod || "PROD";
  const quellStufe = (candData?.candidate?.sammelStufe || "").toUpperCase()
    || (project?.stageEnvMap?.qas ? project.stageEnvMap.qas : "QAS");

  // One candidate per package. Since the plant rework every plant is its own package with its
  // own state, so plant 31 and plant 33 appear separately and releasing one leaves the other
  // untouched. Before, the candidate merged everything into one entry and the page had to block
  // as soon as more than one package had accumulated.
  const candidate = candData?.candidate || null;
  const kandidaten = Array.isArray(candidate?.kandidaten) ? candidate.kandidaten : [];

  const histItems = Array.isArray(histData?.items) ? histData.items : [];
  const histTotal = histData?.total || histItems.length;

  const configured = !!candData?.configured;
  const tokenSet = candData?.tokenSet !== false;
  const kandidatVorhanden = kandidaten.length > 0;

  // Counters for the basin bar (honest numbers only, no guesses).
  const offenCount = kandidaten.length;
  const ausgefuehrtCount = histTotal;

  // Opens the detail sheet. History entries carry all fields in the
  // response; for the candidate, result and reason get reset.
  const openCandidate = (k) => {
    if (!k) return;
    setResult(null); setGrund("");
    setDetail({ kind: "candidate", data: k });
  };
  const openHistory = (item) => {
    setResult(null); setGrund("");
    setDetail({ kind: "history", data: item });
  };

  const freigeben = async () => {
    if (!candPayload) { addToast(t("freigabe:meldung.kandidatFehler"), "error"); return; }
    if (grund.trim().length < 3) { addToast(t("freigabe:meldung.grundFehlt"), "error"); return; }
    setReleasing(true); setResult(null);
    try {
      // approvalNote is the proof of the release. It goes to the server
      // exactly as the approver typed it - never translated.
      const r = await api("/api/cicd/transport/release", {
        method: "POST",
        body: JSON.stringify({ spaceId, ...candPayload, approvalNote: grund.trim() }),
      });
      setResult({ ok: true, tag: r?.tag, runUrl: r?.run?.html_url || null });
      addToast(t("freigabe:meldung.freigegeben", { ziel: prodEnv }), "success");
      // After the release: reload candidate and history.
      ladeKandidat(spaceId);
      ladeHistorie(spaceId, histLimit);
    } catch (e) {
      setResult({ ok: false, error: e?.message || "" });
      addToast(e?.message || t("freigabe:meldung.fehlgeschlagen"), "error");
    }
    setReleasing(false);
  };

  // Group the history by day.
  const histChapters = useMemo(() => {
    const map = new Map();
    for (const it of histItems) {
      if (!it.at) continue;
      const k = dayKey(it.at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    return Array.from(map.entries());
  }, [histItems]);

  if (user && !isAdmin) {
    return (
      <div className="iio-slz">
        <ToastContainer toasts={toasts} />
        <div className="iio-slz-empty" style={{ maxWidth: 440, margin: "80px auto" }}>
          <h3>{t("freigabe:zugriff.titel")}</h3>
          <p>{t("freigabe:zugriff.text")}</p>
        </div>
      </div>
    );
  }

  // Detail derivation
  const d = detail?.data;
  const detailIsCand = detail?.kind === "candidate";
  const detailIsHist = detail?.kind === "history";

  // Display building blocks for the OPENED candidate (the detail sheet always shows exactly one).
  const aktKandidat = detailIsCand ? d : null;
  const candItems = aktKandidat?.items || [];
  const candSummary = aktKandidat?.summary || { iflows: 0, pushes: 0, laeufe: 0 };
  const candPayload = aktKandidat?.releasePayload || null;
  const kandidatName = aktKandidat?.packageName || aktKandidat?.packageId || null;

  const detailPaket = detailIsHist
    ? (d?.packageName || d?.packageId || t("schleuse:blatt.keinPaket"))
    : (kandidatName || t("schleuse:blatt.keinPaket"));

  const detailArtefakte = detailIsHist
    ? (Array.isArray(d?.artifactIds) ? d.artifactIds : [])
    : (candItems.map(x => x.iflow));

  const detailQuelle = detailIsHist ? (d?.sourceEnv || quellStufe) : quellStufe;
  const detailZiel = detailIsHist ? (d?.targetEnv || prodEnv) : prodEnv;
  const detailPartner = detailIsHist && Array.isArray(d?.partnerIds) ? d.partnerIds : [];

  return (
    <div className="iio-slz">
      <ToastContainer toasts={toasts} />

      {/* Header */}
      <div className="iio-page-head">
        <div className="iio-page-head-left iio-anim-arrival">
          <div className="iio-breadcrumb">
            <span className="iio-accent">{t("freigabe:kopf.brotkrume")}</span>
            <span className="iio-sep">/</span>
            <span>{t("freigabe:kopf.brotkrumeAktuell")}</span>
          </div>
          <h1>{t("freigabe:kopf.titel")}</h1>
          <p className="iio-lead">{t("freigabe:kopf.einleitung", { ziel: prodEnv })}</p>
        </div>
      </div>

      {/* Space picker (bar). Deliberately plain: one line, one purpose. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 12px", flexWrap: "wrap" }}>
        <select
          value={spaceId}
          onChange={e => setSpaceId(e.target.value)}
          style={{
            height: 36, padding: "0 30px 0 12px", border: "1px solid var(--iio-border)",
            borderRadius: "var(--iio-r-sm)", background: "var(--iio-bg-1)",
            fontSize: 13.5, color: "var(--iio-text-1)", appearance: "auto", outline: "none",
            minWidth: 240, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <option value="">{t("freigabe:leiste.spaceWaehlen")}</option>
          {spaces.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
        </select>
      </div>

      {/* Basins: only open and released */}
      <div className="iio-slz-basins">
        <button type="button" className={"iio-slz-basin" + (basin === "offen" ? " on" : "")} onClick={() => setBasin("offen")}>
          <div className="b-lbl">{t("schleuse:becken.offen")}</div>
          <div className="b-val">{offenCount}</div>
          <div className="b-sub">
            {offenCount > 0 ? t("schleuse:becken.wartetFreigabe") : t("schleuse:becken.nichtsOffen")}
          </div>
        </button>
        <button type="button" className={"iio-slz-basin" + (basin === "ausgefuehrt" ? " on" : "")} onClick={() => setBasin("ausgefuehrt")}>
          <div className="b-lbl">{t("schleuse:becken.ausgefuehrt")}</div>
          <div className="b-val">{ausgefuehrtCount}</div>
          <div className="b-sub">
            {ausgefuehrtCount > 0
              ? t("schleuse:becken.freigabenGesamt", { count: ausgefuehrtCount })
              : t("schleuse:becken.nichtsAusgefuehrt")}
          </div>
        </button>
      </div>

      {/* States above the list: no space, loading, setup missing. */}
      {!spaceId ? (
        <div className="iio-slz-empty">
          <h3>{t("freigabe:zustand.keinSpaceTitel")}</h3>
          <p>{t("freigabe:zustand.keinSpaceText")}</p>
        </div>
      ) : (candLoading && !candData) ? (
        <div className="iio-slz-empty"><p>{t("freigabe:zustand.laedt")}</p></div>
      ) : !configured ? (
        <div className="iio-slz-empty">
          <h3>{t("freigabe:zustand.keinePipelineTitel")}</h3>
          <p>{t("freigabe:zustand.keinePipeline")}</p>
          <p style={{ marginTop: 12 }}>
            <a href="/cicd-setup" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>
              {t("freigabe:zustand.zumSetup")}
            </a>
          </p>
        </div>
      ) : (
        <div className="iio-slz-list" key={basin}>

          {/* Basin "open": at most ONE candidate per space. */}
          {basin === "offen" && (
            <>
              {!kandidatVorhanden ? (
                <div className="iio-slz-empty">
                  <h3>{t("schleuse:liste.leerOffenTitel")}</h3>
                  <p>{candidate?.lastRelease?.tag
                    ? t("schleuse:liste.leerOffenSeitTag", { tag: candidate.lastRelease.tag })
                    : t("schleuse:liste.leerOffenSeitBeginn")}</p>
                </div>
              ) : (
                <section className="iio-slz-chap">
                  <div className="chap-head">
                    <span className="d">{t("schleuse:eintrag.kandidat")}</span>
                    <span className="ln" />
                    <span className="s">{t("schleuse:eintrag.kandidatenZahl", { count: kandidaten.length })}</span>
                  </div>
                  {kandidaten.map((k) => {
                    const kSeit = k.lastRelease?.tag
                      ? t("schleuse:eintrag.seitTag", { tag: k.lastRelease.tag })
                      : t("schleuse:eintrag.seitBeginn");
                    return (
                      <div className="iio-slz-row" key={k.packageId}>
                        <div className="iio-slz-req iio-anim-stagger" onClick={() => openCandidate(k)}>
                          <div className="req-time">
                            <div className="t">{k.summary.iflows}</div>
                            <div className="u">{t("schleuse:eintrag.iflowsZahl", { count: k.summary.iflows })}</div>
                          </div>
                          <span className="req-bar is-wait" />
                          <div className="req-main">
                            <div className="cat">
                              {t("schleuse:eintrag.kandidat")} · {quellStufe} → {prodEnv}
                            </div>
                            <div className="ttl">{k.packageName || k.packageId}</div>
                            <div className="mini-route">
                              <span className="mini-node src">{quellStufe}</span>
                              <span style={{ color: "var(--iio-text-3)" }}>→</span>
                              <span className="mini-node dst">{prodEnv}</span>
                              <span className="meta">
                                · {t("schleuse:eintrag.artefakteZahl", { count: k.items.length })}
                                {" · "}{t("schleuse:eintrag.pushesGesamt", { count: k.summary.pushes || 0 })}
                                {" · "}{kSeit}
                              </span>
                            </div>
                          </div>
                          <span className="req-chev">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                          </span>
                        </div>
                        <div className="iio-slz-margin" />
                      </div>
                    );
                  })}
                </section>
              )}
            </>
          )}

          {/* Basin "released": history grouped by day. */}
          {basin === "ausgefuehrt" && (
            <>
              {histLoading && !histData && <div className="iio-slz-empty"><p>{t("schleuse:liste.laedt")}</p></div>}
              {!histLoading && histItems.length === 0 && (
                <div className="iio-slz-empty">
                  <h3>{t("schleuse:liste.leerAusgefuehrtTitel")}</h3>
                  <p>{t("schleuse:liste.leerAusgefuehrtText", { ziel: prodEnv })}</p>
                </div>
              )}
              {!histLoading && histChapters.map(([day, items], ci) => (
                <section className="iio-slz-chap" key={day}>
                  <div className="chap-head">
                    <span className="d">{fmtDay(day, t)}</span>
                    <span className="ln" />
                    <span className="s">{t("schleuse:liste.eintrag", { count: items.length })}</span>
                  </div>
                  {items.map((r, i) => {
                    const paketName = r.packageName || r.packageId || t("schleuse:blatt.keinPaket");
                    return (
                      <div className="iio-slz-row" key={r.runId + "-" + i}>
                        <div
                          className="iio-slz-req iio-anim-stagger"
                          style={{ animationDelay: `${Math.min(i + ci * 3, 8) * 40}ms` }}
                          onClick={() => openHistory(r)}
                        >
                          <div className="req-time">
                            <div className="t">{fmtClock(r.at)}</div>
                            <div className="u">{r.approvedBy?.name || ""}</div>
                          </div>
                          <span className="req-bar is-ok" />
                          <div className="req-main">
                            <div className="cat">
                              {/* No name stored (older entry): leave out the "by" part
                                  instead of claiming "unknown" - only show what is there. */}
                              {r.approvedBy?.name
                                ? t("schleuse:blatt.metaFreigabe", { zeit: fmtFull(r.at), name: r.approvedBy.name, ziel: r.targetEnv || prodEnv })
                                : t("schleuse:blatt.metaFreigabeOhne", { zeit: fmtFull(r.at), ziel: r.targetEnv || prodEnv })}
                            </div>
                            <div className="ttl">{paketName}</div>
                            <div className="mini-route">
                              <span className="mini-node src">{r.sourceEnv || quellStufe}</span>
                              <span style={{ color: "var(--iio-text-3)" }}>→</span>
                              <span className="mini-node dst">{r.targetEnv || prodEnv}</span>
                              <span className="meta">
                                · {t("schleuse:eintrag.artefakteZahl", { count: (r.artifactIds || []).length })}
                                {r.tag && <> · {r.tagUrl
                                  ? <a href={r.tagUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                                      style={{ color: "var(--iio-info)", textDecoration: "none" }} title={t("schleuse:blatt.tagLabel")}>{r.tag}</a>
                                  : r.tag}</>}
                                {/* Direct link to the pipeline run of the release: the core of
                                    the traceability, so it sits in the row and not only in the
                                    detail. stopPropagation: the row opens the sheet. */}
                                {r.runUrl && <> · <a href={r.runUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                                  style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>{t("schleuse:eintrag.laufLink")}</a></>}
                              </span>
                            </div>
                          </div>
                          <span className="req-chev">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                          </span>
                        </div>
                        <div className="iio-slz-margin">
                          {r.approvalNote && (
                            <div className="flag"><span>
                              {r.approvalNote}
                              {r.approvedBy?.name && <div className="meta">{r.approvedBy.name}</div>}
                            </span></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </section>
              ))}
              {/* "Load more" only when the server says there is more. */}
              {!histLoading && histTotal > histItems.length && (
                <div style={{ textAlign: "center", padding: "18px 0 6px" }}>
                  <button
                    type="button"
                    className="iio-btn iio-btn--secondary"
                    onClick={() => {
                      const next = histLimit * 2;
                      setHistLimit(next);
                      ladeHistorie(spaceId, next);
                    }}
                  >
                    {t("schleuse:liste.mehrLaden")}
                  </button>
                  <div style={{ fontSize: 11, color: "var(--iio-text-3)", marginTop: 6 }}>
                    {t("schleuse:liste.mehrHinweis", { gezeigt: histItems.length, gesamt: histTotal })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Detail sheet */}
      {detail && (
        <>
          <div className="iio-slz-veil" onClick={closeSheet} />
          <aside className="iio-slz-sheet" role="dialog" aria-modal="true">
            <div className="sheet-head">
              <button type="button" className="sheet-back" onClick={closeSheet}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m14 6-6 6 6 6" /></svg>
                {t("schleuse:blatt.zurueck")}
              </button>
              <div className="sheet-title">
                <span className={"bar " + (detailIsCand ? "is-wait" : "is-ok")} />
                <h2>{detailIsHist
                  ? t("schleuse:blatt.titelHistorie", { paket: detailPaket })
                  : t("schleuse:blatt.titelKandidat", { paket: detailPaket })}</h2>
              </div>
              <div className="sheet-meta">
                {detailIsHist
                  ? (d.approvedBy?.name
                      ? t("schleuse:blatt.metaFreigabe", { zeit: fmtFull(d.at), name: d.approvedBy.name, ziel: d.targetEnv || prodEnv })
                      : t("schleuse:blatt.metaFreigabeOhne", { zeit: fmtFull(d.at), ziel: d.targetEnv || prodEnv }))
                  : t("schleuse:blatt.meta", { quelle: detailQuelle, ziel: detailZiel })}
              </div>
            </div>

            <div className="sheet-body">
              {/* Route */}
              <div className="sec iio-anim-stagger">
                <div className="sec-h">{t("schleuse:blatt.routeTitel")}</div>
                <div className="iio-slz-routemap">
                  <div className="tenant src">
                    <div className="tn">{detailQuelle}</div>
                    <div className="ts">{t("schleuse:blatt.quelle")}</div>
                  </div>
                  <span style={{ flex: "0 0 16px" }} />
                  <div className="route-fan">
                    <div className="route-line">
                      <span className="rl-stem" />
                      <span style={{ flex: "0 0 12px" }} />
                      <div className="tenant dst">
                        <div className="tn">{detailZiel}</div>
                        <div className="ts">{t("schleuse:blatt.ziel")}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transport contents */}
              <div className="sec iio-anim-stagger" style={{ animationDelay: "40ms" }}>
                <div className="sec-h">{t("schleuse:blatt.inhaltTitel")}</div>
                <dl className="iio-slz-kv">
                  <dt>{t("schleuse:blatt.paketLabel")}</dt>
                  <dd>{detailPaket}</dd>
                  <dt>{t("schleuse:blatt.artefakteLabel")}</dt>
                  <dd>{detailArtefakte.length}</dd>
                  {detailPushes !== null && (
                    <>
                      <dt>{t("schleuse:blatt.pushesLabel")}</dt>
                      <dd>{detailPushes}</dd>
                    </>
                  )}
                  {detailIsHist && detailPartner.length > 0 && (
                    <>
                      <dt>{t("schleuse:blatt.partnerdirLabel")}</dt>
                      <dd>{t("schleuse:blatt.partnerdirZahl", { count: detailPartner.length })}</dd>
                    </>
                  )}
                  {detailIsHist && d.tag && (
                    <>
                      <dt>{t("schleuse:blatt.tagLabel")}</dt>
                      {/* The tag links to the frozen state in the repo: exactly the
                          files that went to PROD with this release. */}
                      <dd>{d.tagUrl
                        ? <a href={d.tagUrl} target="_blank" rel="noreferrer" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>{d.tag}</a>
                        : d.tag}</dd>
                    </>
                  )}
                  {detailIsHist && (
                    <>
                      <dt>{t("schleuse:blatt.runLabel")}</dt>
                      <dd>
                        {d.runUrl
                          ? <a href={d.runUrl} target="_blank" rel="noreferrer" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>{d.runId || t("schleuse:eintrag.kandidat")}</a>
                          : <span style={{ color: "var(--iio-text-3)" }}>{t("schleuse:blatt.runOffen")}</span>}
                      </dd>
                    </>
                  )}
                  {detailIsHist && (
                    <>
                      <dt>{t("schleuse:blatt.begruendungLabel")}</dt>
                      <dd className="note-dd">{d.approvalNote || t("schleuse:blatt.keineBegruendung")}</dd>
                    </>
                  )}
                </dl>

                {detailArtefakte.length > 0 ? (
                  <div className="iio-slz-arts">
                    {detailArtefakte.map((a, ai) => (
                      <div className="art" key={a + "-" + ai}>
                        <span className="an">{a}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="iio-slz-hint">{t("schleuse:blatt.keineArtefakte")}</div>
                )}
              </div>

              {/* Release act, candidate only */}
              {detailIsCand && (
                <div className="iio-slz-approve">
                  <div className="sec-h">{t("freigabe:freigabe.titel")}</div>

                  {/* Show blocking states first, THEN the reason field. Without a
                      token the POST would return 400 - the user should see that
                      before typing. */}
                  {!tokenSet ? (
                    <div className="four-eyes" style={{ borderColor: "var(--iio-critical)" }}>
                      <span>
                        <b>{t("freigabe:zustand.githubGetrenntTitel")}</b>
                        <div style={{ marginTop: 4 }}>{t("freigabe:zustand.githubGetrennt")}</div>
                        <a href="/cicd-setup" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>
                          {t("freigabe:zustand.zumSetup")}
                        </a>
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="four-eyes">
                        <span>{t("freigabe:freigabe.einleitung", { ziel: prodEnv })}</span>
                      </div>
                      <div className="signer">
                        {user?.displayName || user?.username}
                      </div>
                      <label style={{ display: "block", fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--iio-text-3)", marginTop: 8, marginBottom: 6 }}>
                        {t("freigabe:freigabe.grundLabel")}
                      </label>
                      <div className="note-field">
                        <textarea
                          value={grund}
                          onChange={e => setGrund(e.target.value)}
                          placeholder={t("freigabe:freigabe.grundPlatzhalter")}
                        />
                        <div className={"note-count" + (grund.trim().length >= 3 ? " ok" : "")}>
                          {t("freigabe:freigabe.zeichenzahl", { anzahl: grund.trim().length })}
                          {grund.trim().length >= 3 ? "" : t("freigabe:freigabe.mindestzeichen")}
                        </div>
                      </div>
                      <div className="act-row" style={{ marginTop: 14 }}>
                        <button
                          type="button"
                          className="iio-btn iio-btn--signal"
                          disabled={releasing || grund.trim().length < 3 || !candPayload}
                          onClick={freigeben}
                        >
                          {releasing ? t("freigabe:freigabe.laeuft") : t("freigabe:freigabe.knopfFreigeben", { ziel: prodEnv })}
                        </button>
                        <button type="button" className="iio-btn iio-btn--tertiary" onClick={closeSheet}>
                          {t("freigabe:freigabe.knopfSpaeter")}
                        </button>
                      </div>

                      {result && result.ok && (
                        <div className="iio-slz-confirm" style={{ borderColor: "var(--iio-success)" }}>
                          <div className="cc-t">{t("freigabe:ergebnis.erfolgTitel")}</div>
                          <div className="cc-b">
                            {t("freigabe:ergebnis.erfolgTag", { tag: result.tag || "" })}
                          </div>
                          {result.runUrl && (
                            <a href={result.runUrl} target="_blank" rel="noreferrer" style={{ color: "var(--iio-info)", fontWeight: 600, textDecoration: "none" }}>
                              {t("freigabe:ergebnis.erfolgLauf")}
                            </a>
                          )}
                        </div>
                      )}
                      {result && !result.ok && (
                        <div className="iio-slz-confirm" style={{ borderColor: "var(--iio-critical)" }}>
                          <div className="cc-t">{t("freigabe:ergebnis.fehlerTitel")}</div>
                          <div className="cc-b">{t("freigabe:ergebnis.fehlerHinweis", { fehler: result.error || "" })}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
